/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTenantSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling tenant form submission and deletion.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createTenant, updateTenant, deleteTenant } from '../api/tenantsApi';
import type {
  TenantFormState,
  DomainFormState,
  UserFormState
} from './useTenantData';
import type { Tenant } from '../types';

const sanitizeDomainFormState = (state: DomainFormState): DomainFormState => ({
  domainName: state.domainName.trim(),
  domainDescription: state.domainDescription.trim(),
  language: state.language.trim() || 'en',
  timezone: state.timezone.trim() || 'UTC',
  countryCode: state.countryCode.trim() || '1'
});

const sanitizeUserFormState = (state: UserFormState): UserFormState => ({
  username: state.username.trim(),
  email: state.email.trim(),
  password: state.password.trim(),
  fullName: state.fullName.trim(),
  department: state.department.trim(),
  locale: state.locale.trim() || 'es',
  timezone: state.timezone.trim() || 'UTC',
  startupDialog: state.startupDialog.trim() || 'dashboard',
  totpEnabled: Boolean(state.totpEnabled),
  isActive: Boolean(state.isActive),
  profileId: state.profileId.trim(),
  logProfileId: state.logProfileId.trim(),
  tenantIds: state.tenantIds ?? []
});

export function useTenantSubmit(
  mode: 'create' | 'edit',
  id: string | undefined,
  formData: TenantFormState,
  domainData: DomainFormState,
  userData: UserFormState,
  useCurrentUser: boolean,
  sanitizeTenantFormState: (state: TenantFormState) => TenantFormState,
  setFormData: React.Dispatch<React.SetStateAction<TenantFormState>>,
  setOriginalData: React.Dispatch<React.SetStateAction<TenantFormState>>,
  validateForm: () => boolean,
  hasChanges: boolean
) {
  const { user } = useAuthStore();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<Tenant | undefined> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Check if there are changes
      // hasChanges validation removed

    if (!validateForm()) {
      showError('Please fix the errors in the form before continuing.');
      return;
    }

    try {
      setIsSubmitting(true);

      const sanitizedTenant = sanitizeTenantFormState(formData);

      // Build payload with proper field mapping and optional field handling
      const payload: Record<string, unknown> = {
        name: sanitizedTenant.name,
        slug: sanitizedTenant.slug,
        description: sanitizedTenant.description || undefined,
        adminEmail: sanitizedTenant.adminEmail || undefined,
        defaultLocale: sanitizedTenant.defaultLocale,
        timezone: sanitizedTenant.timezone,
        isActive: sanitizedTenant.isActive,
        maxExtensions: sanitizedTenant.maxExtensions,
        maxTrunks: sanitizedTenant.maxTrunks,
        maxQueues: sanitizedTenant.maxQueues,
        maxIvr: sanitizedTenant.maxIvr,
        maxConferences: sanitizedTenant.maxConferences,
        maxParkingLots: sanitizedTenant.maxParkingLots,
        allowRecording: sanitizedTenant.allowRecording
        // Note: Retention/cleanup settings are managed separately in the System Cleanup module
      };

      if (mode === 'create') {
        const sanitizedDomain = sanitizeDomainFormState(domainData);
        if (sanitizedDomain.domainName && sanitizedDomain.domainName.trim()) {
          payload.domain = {
            domainName: sanitizedDomain.domainName.trim(),
            domainDescription: sanitizedDomain.domainDescription?.trim() || undefined,
            language: sanitizedDomain.language || undefined,
            timezone: sanitizedDomain.timezone || undefined,
            countryCode: sanitizedDomain.countryCode || undefined
          };
        }

        if (!useCurrentUser) {
          const sanitizedUser = sanitizeUserFormState(userData);
          if (!sanitizedUser.username || !sanitizedUser.email || !sanitizedUser.password) {
            showError(t('tenants.validationError'));
            setIsSubmitting(false);
            return undefined;
          }

          payload.user = {
            username: sanitizedUser.username,
            email: sanitizedUser.email,
            password: sanitizedUser.password,
            fullName: sanitizedUser.fullName || undefined,
            department: sanitizedUser.department || undefined,
            locale: sanitizedUser.locale || undefined,
            timezone: sanitizedUser.timezone || undefined,
            startupDialog: sanitizedUser.startupDialog || undefined,
            totpEnabled: sanitizedUser.totpEnabled,
            isActive: sanitizedUser.isActive
          };
        }
      }

      const result = mode === 'create'
        ? await createTenant(tenantSlug, user?.id ? Number(user.id) : 0, payload)
        : await updateTenant(tenantSlug, user?.id ? Number(user.id) : 0, id!, payload);

      showSuccess(
        mode === 'create'
          ? t('tenants.messages.createSuccess')
          : t('tenants.messages.updateSuccess')
      );

      const normalizedState = sanitizeTenantFormState(sanitizedTenant);
      setFormData(normalizedState);
      setOriginalData(normalizedState);

      // Return the saved tenant data
      return result.data as Tenant;
    } catch (error) {
      let errorMessage = t('tenants.failedToSaveTenant');

      // Check for specific domain error by error code or message
      if (error?.errorCode === 'DOMAIN_ALREADY_EXISTS' ||
        error?.message?.includes('DOMAIN_ALREADY_EXISTS') ||
        error?.message?.includes('domain already exists') ||
        error?.message?.toLowerCase().includes('a domain with this name already exists')) {
        errorMessage = t('tenants.domainAlreadyExists', 'A domain with this name already exists. Please choose a different domain name.');
      } else if (error?.errorCode === 'DOMAIN_CREATION_UNAVAILABLE' ||
        error?.message?.includes('DOMAIN_CREATION_UNAVAILABLE')) {
        errorMessage = t('tenants.domainCreationUnavailable', 'Telephony database is not available. Tenant was created but domain could not be created.');
      } else {
        // Check if error has details (from API response)
        if (error?.details && Array.isArray(error.details) && error.details.length > 0) {
          const firstError = error.details[0];
          errorMessage = firstError.message || error?.message || errorMessage;
        } else {
          errorMessage = error?.message || errorMessage;
        }
      }

      showError(errorMessage);
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    mode,
    id,
    formData,
    domainData,
    userData,
    useCurrentUser,
    sanitizeTenantFormState,
    validateForm,
    hasChanges,
    tenantSlug,
    user?.id,
    showSuccess,
    showError,
    showInfo,
    t,
    setFormData,
    setOriginalData
  ]);

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteTenant(tenantSlug, user?.id ? Number(user.id) : 0, id);
      showSuccess(t('tenants.messages.deleteSuccess') || 'Tenant deleted successfully');
    } catch (error) {
      showError(error.message || t('tenants.messages.deleteError') || 'Failed to delete tenant');
    } finally {
      setIsDeleting(false);
    }
  }, [id, tenantSlug, user?.id, showSuccess, showError, t]);

  return {
    handleSubmit,
    handleDelete,
    isSubmitting,
    isDeleting
  };
}

